"use client";

import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { COUNTRIES } from "@/lib/constants/countries";

const NORDIC_CODES = ["SE", "NO", "DK", "FI", "IS"];
const NORDIC_COUNTRIES = COUNTRIES.filter((c) => NORDIC_CODES.includes(c.code));
const OTHER_COUNTRIES = COUNTRIES.filter((c) => !NORDIC_CODES.includes(c.code));
import type { Partner, PartnerFormValues } from "./types";
import { INVOICE_REFERENCE_MAX_LENGTH } from "@/lib/constants/accounting";

const partnerSchema = z.object({
  company_name: z.string().min(1, "Company name is required"),
  organization_number: z.string().optional().or(z.literal("")),
  address_line_1: z.string().min(1, "Address is required"),
  address_line_2: z.string().optional().or(z.literal("")),
  city: z.string().min(1, "City is required"),
  postcode: z.string().min(1, "Postcode is required"),
  country: z.string().length(2, "Select a country"),
  invoice_reference: z.string().optional().or(z.literal("")),
  commission_percentage: z
    .number({ invalid_type_error: "Commission % is required" })
    .min(0, "Min 0%")
    .max(100, "Max 100%"),
  payment_terms_days: z
    .number({ invalid_type_error: "Payment term is required" })
    .int("Whole days only")
    .min(0, "Min 0 days")
    .max(365, "Max 365 days"),
});

interface PartnerFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  partner: Partner | null;
  onSubmit: (values: PartnerFormValues) => void | Promise<void>;
  submitting?: boolean;
}

const DEFAULT_VALUES: PartnerFormValues = {
  company_name: "",
  organization_number: "",
  address_line_1: "",
  address_line_2: "",
  city: "",
  postcode: "",
  country: "SE",
  invoice_reference: "",
  commission_percentage: 10,
  payment_terms_days: 20,
};

export function PartnerFormDialog({
  open,
  onOpenChange,
  partner,
  onSubmit,
  submitting = false,
}: PartnerFormDialogProps) {
  const isEdit = !!partner;

  const form = useForm<PartnerFormValues>({
    resolver: zodResolver(partnerSchema),
    defaultValues: DEFAULT_VALUES,
  });

  useEffect(() => {
    if (open) {
      form.reset(
        partner
          ? {
              company_name: partner.company_name,
              organization_number: partner.organization_number ?? "",
              address_line_1: partner.address_line_1,
              address_line_2: partner.address_line_2 ?? "",
              city: partner.city,
              postcode: partner.postcode,
              country: partner.country,
              invoice_reference: partner.invoice_reference ?? "",
              commission_percentage: partner.commission_percentage,
              payment_terms_days: partner.payment_terms_days ?? 20,
            }
          : DEFAULT_VALUES,
      );
    }
  }, [open, partner, form]);

  // Don't auto-close; the parent decides based on the API result so we
  // keep the form open (with errors) on failure.
  const handleSubmit = form.handleSubmit(async (values) => {
    await onSubmit(values);
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Edit partner" : "Add new partner"}
          </DialogTitle>
          <DialogDescription>
            Partners receive a fixed commission on show fees and have their
            invoice information prefilled when creating bookings.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 py-2">
          <div className="space-y-2">
            <Label htmlFor="company_name">Company name *</Label>
            <Input
              id="company_name"
              {...form.register("company_name")}
              placeholder="e.g., Acme Events AB"
            />
            {form.formState.errors.company_name && (
              <p className="text-xs text-red-600">
                {form.formState.errors.company_name.message}
              </p>
            )}
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="organization_number">Org. number</Label>
              <Input
                id="organization_number"
                {...form.register("organization_number")}
                placeholder="556677-8899"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="commission_percentage">Commission % *</Label>
              <Input
                id="commission_percentage"
                type="number"
                step="0.5"
                min="0"
                max="100"
                {...form.register("commission_percentage", {
                  valueAsNumber: true,
                })}
              />
              {form.formState.errors.commission_percentage && (
                <p className="text-xs text-red-600">
                  {form.formState.errors.commission_percentage.message}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="payment_terms_days">Payment term (days) *</Label>
              <Input
                id="payment_terms_days"
                type="number"
                step="1"
                min="0"
                max="365"
                {...form.register("payment_terms_days", {
                  valueAsNumber: true,
                })}
              />
              {form.formState.errors.payment_terms_days && (
                <p className="text-xs text-red-600">
                  {form.formState.errors.payment_terms_days.message}
                </p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="address_line_1">Address line 1 *</Label>
            <Input
              id="address_line_1"
              {...form.register("address_line_1")}
              placeholder="Storgatan 1"
            />
            {form.formState.errors.address_line_1 && (
              <p className="text-xs text-red-600">
                {form.formState.errors.address_line_1.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="address_line_2">Address line 2</Label>
            <Input id="address_line_2" {...form.register("address_line_2")} />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="postcode">Postcode *</Label>
              <Input id="postcode" {...form.register("postcode")} />
              {form.formState.errors.postcode && (
                <p className="text-xs text-red-600">
                  {form.formState.errors.postcode.message}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="city">City *</Label>
              <Input id="city" {...form.register("city")} />
              {form.formState.errors.city && (
                <p className="text-xs text-red-600">
                  {form.formState.errors.city.message}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="country">Country *</Label>
              <Select
                value={form.watch("country")}
                onValueChange={(v) => form.setValue("country", v)}
              >
                <SelectTrigger id="country">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectGroup>
                    <SelectLabel className="text-xs text-gray-500">
                      Nordic
                    </SelectLabel>
                    {NORDIC_COUNTRIES.map((c) => (
                      <SelectItem key={c.code} value={c.code}>
                        {c.flag} {c.name}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                  <SelectSeparator />
                  <SelectGroup>
                    <SelectLabel className="text-xs text-gray-500">
                      All countries
                    </SelectLabel>
                    {OTHER_COUNTRIES.map((c) => (
                      <SelectItem key={c.code} value={c.code}>
                        {c.flag} {c.name}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2 pt-2 border-t">
            <Label htmlFor="invoice_reference">Invoice reference</Label>
            <Input
              id="invoice_reference"
              {...form.register("invoice_reference")}
              maxLength={INVOICE_REFERENCE_MAX_LENGTH}
              placeholder="PO number or contact name"
            />
          </div>

          <DialogFooter className="pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-purple-600 hover:bg-purple-700"
              disabled={submitting}
            >
              {submitting
                ? isEdit
                  ? "Saving..."
                  : "Creating..."
                : isEdit
                  ? "Save changes"
                  : "Create partner"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
